import { clsx } from 'clsx';
import styles from './Badge.module.scss';

export type Props = {
  /**
   * Content to render inside of `Badge`. This should only contain numeric
   * content! Its type is `string`, however, to support shorthand and formatted
   * content such as "10k" and "10,000"
   */
  children: string;
  /** Optional property for overriding styles. */
  className?: string;
  /**
   * The color variant of `Badge`.
   * @default 'neutral-stronger'
   * */
  variant?:
    | 'neutral'
    | 'primary'
    | 'brand'
    | 'success'
    | 'warning'
    | 'error'
    | 'neutral-stronger'
    | 'primary-stronger'
    | 'brand-stronger'
    | 'success-stronger'
    | 'warning-stronger'
    | 'error-stronger';
};

/**
 * `Badge` renders a small tag component to be used solely for numeric labels.
 *
 * When using this alongside another component, be sure to include the number
 * and what it relates to in a label or aria-label for accessibility.
 *
 * @example
 *
 * <Badge>10,000</Badge>;
 * <Badge variant="brand">3.4k</Badge>;
 * <Badge>{notifcations.length}</Badge>;
 * <Badge>{12}</Badge>;
 */
export const Badge = ({
  className,
  variant = 'neutral-stronger',
  children,
}: Props) => {
  return (
    <span
      className={clsx(styles.badge, className)}
      data-variant={variant}
      data-zds
      // Ignoring contrast here because the new colors for "brand" violate
      // WCAG's contrast algorithm, though they work for APCA
      data-a11y-ignore={
        variant === 'brand-stronger' ? 'color-contrast' : undefined
      }
    >
      {children}
    </span>
  );
};
